import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  SimpleGrid,
  Flex,
  Icon,
  useColorMode,
  useDisclosure,
} from "@chakra-ui/react";
import { FaUsers, FaCalendarAlt, FaShoppingCart, FaFileInvoiceDollar, FaTasks, FaEnvelope, FaPhone, FaChartLine } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { UnifiedLoginModal } from "./authentication/components/UnifiedLoginModal";
import { getColor, getBrandValue } from "../brandConfig";

const Home = () => {
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Theme-aware colors
  const textPrimary = getColor(colorMode === 'light' ? "text.primary" : "text.primaryDark", colorMode);
  const textSecondary = getColor(colorMode === 'light' ? "text.secondary" : "text.secondaryDark", colorMode);
  const primary = getColor("primary", colorMode);
  const cardGradientBg = colorMode === 'light'
    ? `linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 248, 248, 0.9) 50%, rgba(255, 255, 255, 0.95) 100%)`
    : `linear-gradient(135deg, rgba(20, 20, 20, 0.8) 0%, rgba(30, 30, 30, 0.6) 50%, rgba(20, 20, 20, 0.8) 100%)`;
  const cardBorder = colorMode === 'light'
    ? "rgba(0, 0, 0, 0.1)"
    : "rgba(255, 255, 255, 0.1)";

  const modules = [
    { icon: FaUsers, name: "Clients", color: "#A855F7" },
    { icon: FaCalendarAlt, name: "Calendar", color: "#3B82F6" },
    { icon: FaShoppingCart, name: "Products", color: "#FB923C" },
    { icon: FaFileInvoiceDollar, name: "Billing", color: "#22C55E" },
    { icon: FaTasks, name: "Projects", color: "#14B8A6" },
    { icon: FaEnvelope, name: "Email", color: "#6366F1" },
    { icon: FaPhone, name: "Phone", color: "#EC4899" },
    { icon: FaChartLine, name: "Analytics", color: "#EF4444" },
  ];

  const handleLoginSuccess = (token: string) => {
    // Store the authentication token
    localStorage.setItem('auth_token', token);
    // Stay on home page - just close modal
    onClose();
  };

  return (
    <Box
      bg={getColor("background.main")}
      minHeight="100vh"
      position="relative"
    >
      <NavbarWithCallToAction />

      <Container maxW="900px" py={20} position="relative" zIndex={2}>
        <VStack spacing={12} align="center">
          {/* Welcome Section */}
          <Box
            textAlign="center"
            py={12}
            px={6}
          >
            <Heading
              as="h1"
              fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
              fontWeight="600"
              letterSpacing="-0.02em"
              mb={6}
              color={textPrimary}
              fontFamily={getBrandValue("fonts.heading")}
              lineHeight="1.1"
            >
              VanguardiQ Back Office
            </Heading>
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              mb={4}
              color={textSecondary}
              maxW="700px"
              mx="auto"
              fontFamily={getBrandValue("fonts.body")}
              lineHeight="1.6"
            >
              Welcome to the VanguardiQ collaboration platform. This is our back office management system where staff and active partners can login to track business operations and collaborate together.
            </Text>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color={textSecondary}
              maxW="650px"
              mx="auto"
              fontFamily={getBrandValue("fonts.body")}
            >
              If you're here, you're one of our team members or trusted partners.
              Access your tools below to manage our business intelligence platform.
            </Text>
          </Box>

          {/* Modules Grid */}
          <Box width="100%">
            <Heading
              as="h2"
              size="md"
              mb={6}
              textAlign="center"
              color={textPrimary}
              fontWeight="500"
            >
              Our Business Modules
            </Heading>
            <SimpleGrid
              columns={{ base: 2, md: 4 }}
              spacing={6}
              maxW="600px"
              mx="auto"
            >
              {modules.map((module, index) => (
                <Flex
                  key={index}
                  direction="column"
                  align="center"
                  justify="center"
                  p={6}
                  bg={cardGradientBg}
                  backdropFilter="blur(10px)"
                  borderRadius="xl"
                  borderWidth="1px"
                  borderColor={cardBorder}
                  transition="all 0.3s ease"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: `0 8px 24px ${module.color}40`,
                    borderColor: module.color
                  }}
                  cursor="pointer"
                >
                  <Icon
                    as={module.icon}
                    w={8}
                    h={8}
                    color={module.color}
                    mb={3}
                  />
                  <Text
                    fontSize="sm"
                    fontWeight="500"
                    color={textPrimary}
                    textAlign="center"
                  >
                    {module.name}
                  </Text>
                </Flex>
              ))}
            </SimpleGrid>
          </Box>

          {/* Login CTA */}
          <Box
            textAlign="center"
            py={8}
            px={8}
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            borderRadius="xl"
            borderWidth="1px"
            borderColor={cardBorder}
            width="100%"
            maxW="500px"
          >
            <Heading as="h3" size="md" mb={4} color={textPrimary}>
              Ready to Get Started?
            </Heading>
            <Text fontSize="md" mb={6} color={textSecondary}>
              Login to access your business management dashboard and collaborate with your team.
            </Text>
            <Button
              size="lg"
              bg={primary}
              color="white"
              width="100%"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "lg"
              }}
              onClick={onOpen}
            >
              Login to Dashboard
            </Button>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />

      {/* Login Modal */}
      <UnifiedLoginModal
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={handleLoginSuccess}
      />
    </Box>
  );
};

export default Home;
